// ...removed 'use client'; for CRA compatibility...
import React, { useEffect, useRef } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { LuzmoDashboardComponent } from '@luzmo/react-embed';
import formbricks from '@formbricks/js';

interface Props {
  authKey: string;
  authToken: string;
  dashboardId: string;
  email: string;
  jwt: string;
  panoId: string;
}

export default function Pano({ authKey, authToken, dashboardId, email, jwt, panoId }: Props) {

  const dashboardRef = useRef<any>(null);

  useEffect(() => {
    console.log("Pano mounted with dashboardId:", dashboardId);
  }, [dashboardId]);

  if (!dashboardId) return null;

  const onLoad = (e: any) => {
    console.log('Dashboard load event:', e.detail);
  };

  const onChangedFilters = (e: any) => {
    const filters = e.detail;
    console.log('Filters changed:', filters);

    fetch('https://rpa.dataritim.com/webhook/sessions/update_ds', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwt}`
      },
      body: JSON.stringify({
        email: email,
        panoId: panoId,
        filters: filters
      }),
    })
      .then((resp) => resp.text())
      .then((data) => {
        console.log("Saved filters to database:", data);
      })
      .catch((err) => {
        console.error("Failed to save filters", err);
      });
  };

  const onCustomEvent = (e: any) => {
    console.log('Custom event:', e.detail);
    // Example payload structure:
    // {
    //    data: {
    //       ...,
    //       payload: {
    //         data: {
    //           event: "contact"
    //         }
    //       }
    //    }
    // }
    const eventData = e.detail?.data?.payload?.data;
    if (eventData && typeof eventData.event === 'string' && eventData.event.startsWith('fba')) {
      // Trigger Formbricks action for any event starting with "fba"
      formbricks.track(eventData.event);
      console.log(`Formbricks event '${eventData.event}' triggered.`);
    } else if (eventData && eventData.event === 'refresh') { 
      // Refresh the dashboard
      dashboardRef.current.refreshData()
        .then(() => {
          console.log("Dashboard data has been refreshed!");
        })
        .catch((err: any) => {
          console.error("Failed to refresh data:", err);
        });
    } else if (eventData && eventData.event) {
      // Handle other events: send to backend, show notification, reload if needed
      const url = `https://rpa.dataritim.com/webhook/${eventData.event}`;
      const payload = {
        ...e.detail,
        email: email,
        panoId: panoId
      };
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwt}`
        },
        body: JSON.stringify(payload)
      })
        .then(async (resp) => {
          const data = await resp.json();
          // Show notification
          toast(data.message, { position: 'top-right', autoClose: data.duration ? data.duration * 1000 : 5000 });
          // Reload dashboard if needed
          if (data.reload && dashboardRef.current && typeof dashboardRef.current.refreshData === 'function') {
            dashboardRef.current.refreshData()
              .then(() => {
                console.log("Dashboard data has been refreshed after event.");
              })
              .catch((err: any) => {
                console.error("Failed to refresh data after event:", err);
              });
          }
        })
        .catch((err) => {
          console.error("Failed to send event to backend", err);
          toast.error("Failed to process event", { position: 'top-right' });
        });
    }
  };

  const onExported = (e: any) => {
    console.log('Export completed or failed:', e.detail);
  };

  const onItemsRendered = (e: any) => {
    console.log('All items in dashboard rendered:', e.detail);
  };

  const onRendered = (e: any) => {
    console.log('Item rendered:', e.detail);
  };

  return (
    <>
      <LuzmoDashboardComponent
        authKey={authKey}
        authToken={authToken}
        dashboardId={dashboardId}
        appServer="https://app.luzmo.com/"
        apiHost="https://api.luzmo.com/"

        loaderBackground="#70d173"
        loaderFontColor="#70d173"
        loaderSpinnerColor="#3d8c41"
        loaderSpinnerBackground="rgba(0,0,0,0.05)"

        language="tr"
        screenMode="auto"
        switchScreenModeOnResize={true}
        timezoneId="Europe/Istanbul"
        editMode='view'
        mainColor="var(--color-accent)"
        accentColor="var(--color-accent-dark)"

        changedFilters={onChangedFilters}
        customEvent={onCustomEvent}
        exported={onExported}
        rendered={onRendered}
        load={onLoad}
        itemsRendered={onItemsRendered}
        
        ref={dashboardRef}
      />
      <ToastContainer />
    </>
  );
}
